import NextLink from "next/link";
import NextImage from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { descending, formatDuration } from "@hackclub/lapse-shared";

import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { TimelapseGrid } from "@/components/entity/TimelapseGrid";

import { api } from "@/api";
import { useAuth } from "@/hooks/useAuth";
import { useCache } from "@/hooks/useCache";
import { useCachedApiCall } from "@/hooks/useCachedApiCall";
import RootLayout from "@/components/layout/RootLayout";

export default function Home() {
  const router = useRouter();
  const auth = useAuth(false);

  const reqLeaderboard = useCachedApiCall(() => api.global.weeklyLeaderboard({}), "leaderboard");
  const reqRecent = useCachedApiCall(() => api.global.recentTimelapses({}), "recent");

  const [totalTimeCache, setTotalTimeCache] = useCache<string>("currentUserTotalTime");
  const [totalTime, setTotalTime] = useState<string | null>(null);
  const [topUserProjects, setTopUserProjects] = useState<{
    name: string,
    time: string,
    percentage: number // [0.0, 1.0], relative to top project. topUserProjects[0].percentage is always 1.0
  }[]>([]);

  // Legacy recordings (unfinished OPFS captures or unpublished drafts) can no longer be recorded, only recovered.
  // The site-wide banner (see RootLayout) nudges the user to `/timelapse/recover` whenever they have any.

  useEffect(() => {
    (async () => {
      if (!auth.currentUser)
        return;

      const res = await api.user.hackatimeProjects({});
      if (!res.ok) {
        console.error("(index.tsx) Could not fetch the user's Hackatime projects!", res);
        return;
      }

      const sorted = res.data.projects
        .toSorted(descending(x => x.time))
        .slice(0, 5);

      setTopUserProjects(
        sorted.map(x => ({
          name: x.name,
          time: formatDuration(x.time),
          percentage: x.time / sorted[0].time
        }))
      );
    })();
  }, [auth.currentUser]);

  useEffect(() => {
    (async () => {
      if (!auth.currentUser)
        return;

      const res = await api.user.getTotalTimelapseTime({ id: auth.currentUser.id });
      if (!res.ok) {
        console.error("(index.tsx) Could not fetch the user's total timelapse time!", res);
        return;
      }

      const duration = formatDuration(res.data.time);
      setTotalTime(duration);
      setTotalTimeCache(duration);
    })();
  }, [auth.currentUser]);

  useEffect(() => {
    const { error } = router.query;

    if (error) {
      router.push(`/auth?error=${error}`);
    }
  }, [router.query, router]);

  function ShelfHeader({ title, description, icon }: {
    title: string,
    description: string,
    icon: string
  }) {
    return (
      <div className="flex items-center gap-4 w-full h-min">
        <img src={icon} alt="" className="block w-14 h-14" />
        
        <div className="flex flex-col">
          <h1 className="font-bold text-3xl">{title}</h1>
          <p className="text-lg sm:text-xl">{description}</p>
        </div>
      </div>
    );
  }

  return (
    <RootLayout showHeader={true}>
      <section className={clsx(
        "p-12 flex-col gap-12", // mobile
        "md:px-16", // tablet
        "lg:px-32 md:py-12 md:flex-row md:gap-0", // desktop
        "flex justify-between items-center w-full bg-grid-gradient border-y border-black" // all
      )}>
        <div className={clsx(
          "flex flex-col min-w-full", // mobile
          "md:flex-row md:w-2/3 md:items-center md:min-w-auto", // desktop
          "gap-8 content-center" // all
        )}>
          <NextImage
            src="/images/orpheus-time.png" alt=""
            width={1200} height={1200}
            className="w-40 h-40"
          />

          {
            auth.currentUser ? (
              <h1 className="text-3xl tracking-tight text-pretty">
                Hi, <b className="sm:text-nowrap">@{auth.currentUser.displayName}</b>! <br />
                { 
                  (totalTime || totalTimeCache)
                    ? (
                      <>
                        You've recorded a total of <br />
                        <b className="sm:text-nowrap">{totalTime || totalTimeCache}</b> of timelapses so far.
                      </>
                    )
                    : (
                      <>
                        You haven't recorded any timelapses so far! <br />
                        Go change that!
                      </>
                    )
                }
              </h1>
            ) : (
              <h1 className="text-3xl tracking-tight">
                Welcome to <b>Lapse</b>, Hack Club's timelapse time tracking tool!
              </h1>
            )
          }
        </div>

        <div className={clsx(
          "w-full", // mobile
          "md:w-1/3", // desktop
          "flex flex-col gap-4 content-around justify-end text-right" // all
        )}>
          {
            auth.currentUser ? (
              topUserProjects.map(x => (
                <div key={x.name} className="flex gap-2.5">
                  <span className="tracking-tight text-nowrap">{x.name}</span>
                  <div className="w-full bg-darkless relative rounded-2xl overflow-hidden">
                    <div
                      style={{ width: `${x.percentage * 100}%` }}
                      className="bg-red text-dark absolute text-right px-4"
                    >
                      {x.time}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="w-full h-full flex justify-end-safe items-center">
                <Button href="/auth" className="px-16" kind="primary" icon="welcome">Sign in</Button>
              </div>
            )
          }
        </div>
      </section>

      <div className="flex flex-col px-12 sm:px-16 md:px-24 lg:px-32 py-8">
        { reqLeaderboard && reqLeaderboard.leaderboard.length != 0 && (
          <section className="flex flex-col w-full">
            <ShelfHeader
              icon="/images/orpheus-cool.png"
              title="Leaderboard"
              description="These Hack Clubbers spent the most time documenting their work!"
            />

            <div className={clsx(
              "flex-wrap py-12", // mobile
              "sm:flex-nowrap md:py-20", // desktop
              "flex w-full gap-y-12", // all
              reqLeaderboard.leaderboard.length <= 2 ? "gap-32" : "justify-between"
            )}>
              {
                reqLeaderboard.leaderboard.slice(0, 6).map((x, i) => (
                  <div
                    key={x.id}
                    className={clsx(
                      "flex flex-col sm:gap-1 justify-center items-center",
                      i === 3 && "min-[583px]:max-[1000px]:hidden",
                      i === 4 && "min-[583px]:max-[1400px]:hidden",
                      i === 5 && "max-[1500px]:hidden",
                    )}
                  >
                    <NextLink href={`/user/@${x.handle}`}>
                      <img
                        src={x.pfp}
                        alt=""
                        className={clsx(
                          "w-16 h-16", // mobile
                          "sm:w-30 sm:h-30", // desktop
                          "block rounded-full mb-2 shadow transition-all hover:brightness-75" // all
                        )}
                      />
                    </NextLink>

                    <div className="text-2xl sm:text-3xl font-bold">{x.displayName}</div>
                    <div className="text-lg sm:text-xl text-center leading-6">{`${formatDuration(x.secondsThisWeek)} recorded`}<br/>this week</div>
                  </div>
                ))
              }
            </div>
          </section>
        ) }

        { reqRecent && reqRecent.timelapses.length != 0 && (
          <section className="flex flex-col w-full gap-12">
            <ShelfHeader
              icon="/images/orpheus-woah.png"
              title="Timelapses Being Created Now"
              description="See what other Hack Clubbers are up to"
            />

            <TimelapseGrid timelapses={reqRecent?.timelapses ?? []} />
          </section>
        ) }

        <Footer />
      </div>
    </RootLayout>
  );
}
